import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { performPostRequest, performGetRequestWithRetry } from '@atlassian/jira-fetch';
import {
	fireTrackAnalytics,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { PACKAGE_NAME, PACKAGE_TEAM_NAME } from '../../common/constants';
import type {
	GettingStartedPageStateProps,
	GettingStartedPageStateResponse,
	LandingPageStateProperties,
} from '../../common/types';

export const initialiseLandingPageState =
	({ projectId, baseUrl, cloudId, createAnalyticsEvent }: GettingStartedPageStateProps) =>
	async (): Promise<void> => {
		try {
			await performPostRequest(
				`${baseUrl}/gateway/api/jsm/onboarding/checklist/v1/cloudid/${cloudId}/project/${projectId}/list/jsm-getting-started-page`,
			);
			fireTrackAnalytics(
				createAnalyticsEvent({
					actionSubject: 'State',
					action: 'initialised',
				}),
				'jiraServicedeskGettingStartedPage.State initialised',
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'State',
					packageName: PACKAGE_NAME,
					teamName: PACKAGE_TEAM_NAME,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

export const getLandingPageState =
	({ projectId, baseUrl, cloudId, createAnalyticsEvent }: GettingStartedPageStateProps) =>
	async (): Promise<GettingStartedPageStateResponse> => {
		try {
			const response = await performGetRequestWithRetry(
				`${baseUrl}/gateway/api/jsm/onboarding/checklist/v1/cloudid/${cloudId}/project/${projectId}/list/jsm-getting-started-page`,
			);

			fireOperationalAnalytics(
				createAnalyticsEvent({
					actionSubject: 'State',
					action: 'fetched',
				}),
				'jiraServicedeskGettingStartedPage.State fetched',
			);
			return response;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'State',
					packageName: PACKAGE_NAME,
					teamName: PACKAGE_TEAM_NAME,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			throw error;
		}
	};

export const updateLandingPageStateProperties =
	({ projectId, baseUrl, cloudId, createAnalyticsEvent }: GettingStartedPageStateProps) =>
	async (properties: LandingPageStateProperties): Promise<void> => {
		const sectionStateString = JSON.stringify(properties);
		try {
			await performPostRequest(
				`${baseUrl}/gateway/api/jsm/onboarding/checklist/v1/cloudid/${cloudId}/project/${projectId}/list/jsm-getting-started-page/properties`,
				{
					body: JSON.stringify({
						sectionState: sectionStateString,
					}),
				},
			);
			fireTrackAnalytics(
				createAnalyticsEvent({
					actionSubject: 'Properties',
					action: 'updated',
				}),
				'jiraServicedeskGettingStartedPage.Properties updated',
				{
					property: properties,
				},
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'Properties',
					packageName: PACKAGE_NAME,
					teamName: PACKAGE_TEAM_NAME,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};
